import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BuySummaryComponent } from './buy-summary/buy-summary.component';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { SharedModule } from 'primeng/api';
import { debounceTime, filter, map, share, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { isElementInViewport, viewportVisibilityChange } from '@digistore/ds24-ui/rxjs/viewport';
import { BehaviorSubject, combineLatest, firstValueFrom, fromEvent, Observable, ReplaySubject, Subject } from 'rxjs';
import { BuyButtonComponent } from './buy-button/buy-button.component';
import { BuyButtonSummarySetting } from './buy-button-wrapper.model';
import { Transaction } from '@elastic/apm-rum';
import * as OrderformActions from '../../ngrx/orderform.actions';
import { Store } from '@ngrx/store';
import {
  getOrder,
  getOrderformSubmitState,
  MonitoringService,
  OrderformState,
  OrderformTrackingCollectorService,
  OrderFormValidationResult,
  SiteOwners,
} from '@orderform/widgets';
import { OrderformIframeService } from '../../services/orderform-iframe.service';
import { IframeModalService } from '../../components/iframe-modal/iframe-modal.service';
import { SessionService } from '../../services/session.service';
import { isPageBuilderElementVisible } from '../../misc/pagebuilder';

@Component({
  selector: 'orderform-buy-button-wrapper',
  templateUrl: './buy-button-wrapper.component.html',
  styleUrls: ['./buy-button-wrapper.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BuySummaryComponent,
    CommonModule,
    DialogModule,
    NgTemplateOutlet,
    SharedModule,
    AsyncPipe,
    NgIf,
    BuyButtonComponent,
  ],
})
export class BuyButtonWrapperComponent implements OnInit, AfterViewInit, OnDestroy {
  SiteOwners = SiteOwners;
  @Input('buy-btn-theme') buyBtnTheme = null;
  @Input() buyBtnText = null;
  @Input() sealType: 'gold' | 'blue' | null = null;

  @Input() iconLeft = null;
  @Input() iconRight = null;

  private _summarySetting$ = new ReplaySubject<BuyButtonSummarySetting>(1);

  @Input() set summarySetting(setting: BuyButtonSummarySetting) {
    this._summarySetting$.next(setting);
  }

  @Input() summaryLayout: 'horizontal' | 'vertical' = 'horizontal';
  @Input() summaryOuterContainerClasses?: string;
  @Input() summaryInnerContainerClasses?: string;

  // null = not in editor
  private _forceShowSummary$ = new BehaviorSubject<'false' | 'true' | null>(null);

  @Input() set forceShowSummary(force: 'false' | 'true' | null) {
    this._forceShowSummary$.next(force);
  }

  /**
   * Attention - dies attribute will be set via javascript. Not via hbs template!
   */
  @Input() set forceShowModal(status: 'true' | 'false') {
    if (status) {
      this.modalVisible = status === 'true';
    }
  }

  @Input() set editorMode(value: string) {
    this._isTranslationMode = value === 'translation';
    this._isEditorMode = value === 'translation' || value === 'design';
  }

  get isTranslationMode(): boolean {
    return this._isTranslationMode;
  }

  private _isTranslationMode: boolean;
  private _isEditorMode = false;

  private _orderformFascade = inject(OrderformFascade);
  private _ngAfterViewInit$ = new ReplaySubject<void>(1);
  private _elRef = inject(ElementRef);
  private _blockButton$ = new BehaviorSubject(false);
  private _store: Store<OrderformState> = inject(Store<OrderformState>);
  private _ngZone = inject(NgZone);
  private _iframeService = inject(OrderformIframeService);
  private _iframeModal = inject(IframeModalService);
  private _monitoring = inject(MonitoringService);
  private readonly sessionService = inject(SessionService);
  private _cdr = inject(ChangeDetectorRef);
  #tracking = inject(OrderformTrackingCollectorService);
  protected _destroy$ = new Subject<void>();

  private sessionId: string;

  public modalVisible = false;
  /**
   * Summary setting
   */
  public readonly modalTitle = $localize`:@@buy_button.buy-verification.title:Sie bestellen`;
  public readonly modalTitleAsGift = $localize`:@@buy_button.buy-verification.title-as-gift:Sie bestellen als Geschenk`;

  public isGift$ = this._orderformFascade.order$.pipe(
    map((order) => {
      return !!order.order.custom_forms.is_gift;
    })
  );

  needToShowOrderSummaryBeforePurchaseByConfig$ = this._orderformFascade.config$.pipe(
    map((data) => {
      return data.show_order_summary_before_purchase;
    })
  );

  scroll$ = fromEvent(window, 'scroll').pipe(
    takeUntil(this._destroy$),
    debounceTime(100),
    startWith(null),
    map(() => this._isOrderSummaryAndProductListInViewport())
  );

  private _isOrderSummaryAndProductListInViewPort$ = combineLatest([
    this._ngAfterViewInit$.pipe(
      switchMap(() => viewportVisibilityChange(this._elRef.nativeElement, 100)),
      share()
    ),
    this._summarySetting$,
    this.scroll$,
  ]).pipe(
    map(([, summarySetting, isInViewport]) => {
      const testToForce: BuyButtonSummarySetting[] = ['above_if_needed', 'popup_if_needed'];
      if (!testToForce.includes(summarySetting)) {
        return false;
      }
      return isInViewport;
    })
  );

  needToShowOrderSummaryBeforePurchase$: Observable<boolean> = combineLatest([
    this.needToShowOrderSummaryBeforePurchaseByConfig$,
    this._isOrderSummaryAndProductListInViewPort$,
  ]).pipe(
    map(([show_order_summary_before_purchase, forceAllowPurchaseWithoutSummary]) => {
      if (forceAllowPurchaseWithoutSummary) {
        return false;
      }
      return show_order_summary_before_purchase;
    }),
    shareReplay(1)
  );

  showSummary$: Observable<boolean> = combineLatest([
    this._summarySetting$,
    this._forceShowSummary$,
    this.needToShowOrderSummaryBeforePurchase$,
  ]).pipe(
    map(([summarySetting, forceShowSummary, needOverViewBeforePurchase]) => {
      if (forceShowSummary) {
        return forceShowSummary === 'true';
      }

      if (summarySetting === 'above_always') {
        return true;
      }

      if (summarySetting === 'above_if_needed') {
        return needOverViewBeforePurchase;
      }

      if (summarySetting === 'popup_if_needed') {
        return false;
      }
    })
  );

  isCustomTextOnPayButtonAllowed$: Observable<boolean> = this._orderformFascade.config$.pipe(
    map((data) => data.is_pay_button_label_changed_allowed)
  );

  selectedPaymentMethod$ = this._orderformFascade.order$.pipe(
    map((e) => {
      return e.order.payment_provider;
    }),
    shareReplay(1)
  );

  buttonText$ = combineLatest([this.isCustomTextOnPayButtonAllowed$, this.selectedPaymentMethod$]).pipe(
    map(([customTextAllowed, selectedPaymentProv]) => {
      // void == free product
      if (selectedPaymentProv.pay_method === 'void') {
        return $localize`:@@orderform.get_now_for_free:Jetzt kostenlos sichern`;
      }

      // check is allowed to use custom button text
      if (customTextAllowed && this.buyBtnText) {
        return this.buyBtnText;
      } else {
        // use text of selected payment prov.
        // fallback customButtonText
        return (
          selectedPaymentProv.label_data?.default_submit_button.replace('|', '<br/>') ||
          this.buyBtnText ||
          $localize`:@@orderform.buy_now:Jetzt kaufen`
        );
      }
    })
  );

  buttonDisabled$ = combineLatest([this._blockButton$, this.selectedPaymentMethod$]).pipe(
    map(([blockButton, selectedMethod]) => {
      return blockButton || (this._isEditorMode && selectedMethod.disabled);
    })
  );

  resellerId$ = this._orderformFascade.rootState$?.pipe(
    map((e) => e.order.settings.config.siteowner_id),
    shareReplay(1)
  );

  buttonTextBeforeOrderSummary$: Observable<string> = this._orderformFascade.config$.pipe(
    map((data) => data.payment_button_label_overwrite)
  );

  paymentLegalHint$ = combineLatest([this.buttonText$, this._orderformFascade.paymentLegalHint$]).pipe(
    map(([buttonText, hint]) => (hint ? hint.replace('__BUTTON__', buttonText) : null))
  );

  isButtonVisible$ = this.selectedPaymentMethod$.pipe(
    map((selectedMethod) => selectedMethod.is_submit_button_visible || selectedMethod.pay_method === 'alias')
  );

  ngOnInit(): void {
    this.sessionId = this.sessionService.getSessionId();
    this._monitoring.addLabels({ sessionId: this.sessionId });
  }

  ngAfterViewInit() {
    this._ngAfterViewInit$.next();
  }

  ngOnDestroy() {
    this._destroy$.next();
  }

  private _isOrderSummaryAndProductListInViewport(): boolean {
    const orderSummaryEl = document.querySelector('ds-orderform-order-summary');
    const productListEl = document.querySelector('ds-orderform-product-list');

    // If both widgets are not present on the page,
    // return false to indicate they're not in viewport (buy button should show its own summary)
    if (!orderSummaryEl && !productListEl) {
      return false;
    }

    // If only one widget exists, check only that one's viewport status
    // This handles cases where OrderSummary widget exists without CartProductList widget
    if (!productListEl && orderSummaryEl) {
      return isElementInViewport(orderSummaryEl, 0);
    }

    if (!orderSummaryEl && productListEl) {
      return isElementInViewport(productListEl, 0);
    }

    // Both widgets exist, check both are in viewport
    const isOrderSummaryInViewport = isElementInViewport(orderSummaryEl, 0);
    const isProductListInViewport = isElementInViewport(productListEl, 0);

    return isOrderSummaryInViewport && isProductListInViewport;
  }

  private async scrollToFormError() {
    await new Promise((resolve) => {
      this._ngZone.runOutsideAngular(() =>
        setTimeout(async () => {
          const errorElements = document.querySelectorAll(
            'orderform-input-errors .error, ds-orderform-buy-button .error'
          );
          const visibleErrors = Array.from(errorElements).filter((element) =>
            isPageBuilderElementVisible(element as HTMLElement)
          );

          /**
           * Try scrolling to each error element until successful
           * Need for elements that exist twice (desktop/mobile) and only one is visible
           *  */
          for (const errorElement of visibleErrors) {
            const beforeScroll = window.scrollY;

            errorElement.scrollIntoView({ block: 'center' });
            this._iframeService.scrollToIframeElementPosition(errorElement.getBoundingClientRect().top);

            // Wait for scroll animation
            await new Promise((resolve) => setTimeout(resolve, 100));

            const afterScroll = window.scrollY;
            const hasScrolled = Math.abs(afterScroll - beforeScroll) > 5;

            if (hasScrolled) {
              break;
            }
          }

          resolve(true);
        }, 100)
      );
    });
  }

  private async _trackValidationErrors(
    transaction: Transaction,
    validationResults: OrderFormValidationResult[],
    source: 'frontend' | 'backend'
  ) {
    validationResults.forEach((item) => {
      item.invalidFieldList.forEach((field) => {
        field.errors.forEach((err) => {
          this._monitoring.trackValidationError(field.id + ':' + err, source, item.component);
        });
      });
    });
  }

  @HostListener('click', ['$event'])
  public openLegalLink(e: Event) {
    const anchor = e.target as Element;
    const isInstallmentCalculaterButton = e.target['classList'].contains('installment_calculator_button');

    if (anchor.tagName !== 'A' || isInstallmentCalculaterButton) {
      return;
    }
    e.preventDefault();
    this._iframeModal.open(anchor.getAttribute('title') ?? anchor.textContent, anchor.getAttribute('href'));
  }

  public async _beforeSubmitFrontendValidation(nameOfTransaction: string) {
    const orderPaymentRenderer = await firstValueFrom(
      this._orderformFascade.order$.pipe(map((order) => order.order.payment_provider.renderer_type))
    );
    this._monitoring.addLabels({ orderPaymentRenderer });

    const buyNowTransaction = this._monitoring.trackUserInteraction(nameOfTransaction, false);

    this._store.dispatch(OrderformActions.formValidation());
    const validationResult = await this._orderformFascade.formValidation.emit();
    if (!validationResult.reduce((valid, result) => valid && result.valid, true)) {
      this._store.dispatch(OrderformActions.formValidationFailedBeforeSubmit());
      this._trackValidationErrors(buyNowTransaction, validationResult, 'frontend');
      await this.scrollToFormError();
      this._orderformFascade.submitFailed.emit();
      buyNowTransaction.end();
      return false;
    }

    this._store.dispatch(OrderformActions.formValidationSuccess());

    let hasPendingRequest = await firstValueFrom(this._orderformFascade.pending$);
    while (hasPendingRequest) {
      // when a request was pending,
      await firstValueFrom(
        this._orderformFascade.pending$.pipe(
          filter((pending) => !pending),
          debounceTime(50)
        )
      );
      // check again no request is pending
      hasPendingRequest = await firstValueFrom(this._orderformFascade.pending$);
    }

    buyNowTransaction.end();
    const beforeSubmitHookTransaction = this._monitoring.trackUserInteraction('BeforeSubmitHook', false);

    // Check Validations
    const canSubmit = await firstValueFrom(this._orderformFascade.beforeSubmitOrderForm());

    if (!canSubmit) {
      await this.scrollToFormError();
      this._orderformFascade.submitFailed.emit();

      this._trackValidationErrors(buyNowTransaction, validationResult, 'frontend');
      beforeSubmitHookTransaction.end();
      return false;
    }

    beforeSubmitHookTransaction.end();
    return true;
  }

  public async showSummaryDisalog() {
    if (this._isEditorMode) {
      return;
    }

    this._store.dispatch(OrderformActions.normalCheckoutButtonClicked());

    /**
     * Block double clicks on buy button
     */
    this._blockButton$.next(true);

    this._monitoring.addLabels({ orderformSummary: 'dialog' });

    this.#tracking.track('BuyShowSummary', { orderFormSummaryType: 'dialog' });
    const valid = await this._beforeSubmitFrontendValidation('BuyShowSummary');
    this._blockButton$.next(false);

    if (!valid) {
      return;
    }

    /**
     * Modal for summary before purchase check
     */
    this.modalVisible = true;
  }

  public async submit(trackWithDialog = false) {
    if (this._isEditorMode) {
      return;
    }

    /**
     * Block double clicks on buy button
     */
    let orderFormSummaryType: 'above-button' | 'dialog' | 'none' = 'none';
    this._blockButton$.next(true);
    if (trackWithDialog) {
      orderFormSummaryType = 'dialog';
      this._monitoring.addLabels({ orderformSummary: 'dialog' });
    } else {
      this._store.dispatch(OrderformActions.normalCheckoutButtonClicked());
      const summaryShown = await firstValueFrom(this.needToShowOrderSummaryBeforePurchase$);
      orderFormSummaryType = summaryShown ? 'above-button' : 'none';
      this._monitoring.addLabels({
        orderformSummary: summaryShown ? 'above' : 'none',
      });
    }

    this.#tracking.track('BuyButtonClick', { orderFormSummaryType });
    const valid = await this._beforeSubmitFrontendValidation('BuyButtonClick');

    if (!valid) {
      this._blockButton$.next(false);
      return;
    }

    // Close confirm for loading spinner
    this.modalVisible = false;
    this._cdr.detectChanges();

    const backendSubmitTransaction = this._monitoring.trackUserInteraction('BuySubmit', false);
    const sendSuccess = await firstValueFrom(this._orderformFascade.submitOrderForm());
    // unblock
    this._blockButton$.next(false);

    // Nachdem das Formular abgesendet wurde kann es im Backend noch zu Fehler gekommen sein
    const submitState = await firstValueFrom(this._store.select(getOrderformSubmitState));

    if (submitState === 'failed') {
      this.#tracking.track('BuyBackendSubmitFailed');
      const backendValidationByField = await firstValueFrom(
        this._orderformFascade.validation$.pipe(map((data) => data.errors_by_postname))
      );

      Object.keys(backendValidationByField).forEach((field) => {
        this._monitoring.trackValidationError(field + ':' + backendValidationByField[field], 'backend', 'generic');
      });
      await this.scrollToFormError();
      this._orderformFascade.submitFailed.emit();
      this._store.dispatch(OrderformActions.formValidationFailedAfterSubmit());
      backendSubmitTransaction.end();
      return false;
    } else if (sendSuccess) {
      backendSubmitTransaction.end();
      this._orderformFascade.afterSubmit.emit().then((results) => {
        if (!results.reduce((isValid, result) => isValid && result.valid, true)) {
          this.#tracking.track('BuyBackendSubmitFailed');
          this._store.dispatch(OrderformActions.afterSubmitOrderFormFailed());
        }
      });
    } else {
      backendSubmitTransaction.end();
    }

    await this.addMonitoringLabels(sendSuccess);
  }

  private async addMonitoringLabels(formSubmitSuccess: boolean): Promise<void> {
    const order = await firstValueFrom(this._store.select(getOrder));
    const labels = this._monitoring.getLabelsFromOrderform(order);
    labels.form_submit_result_action = formSubmitSuccess ? 'success' : 'failed';
    this._monitoring.addLabels(labels);
  }
}
